import React, { useEffect, useState, useMemo } from 'react';

const EMPTY_USER = { name: '', email: '', avatarUrl: '', tier: '', role: 'user', credits: [], referredBy: '' };

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const EXPORT_COLUMNS = ['name', 'email', 'tier', 'role', 'avatarUrl', 'referredBy', 'lastLoginAt', 'loginCount', 'creditTotal'];
const IMPORT_COLUMNS = ['name', 'email', 'tier', 'role', 'avatarUrl'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function toCsvValue(value) {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Minimal RFC 4180 reader: handles quoted fields, escaped quotes and CRLF. */
function parseCsv(text) {
  const src = text.replace(/^\uFEFF/, '');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { field += '"'; i += 1; } else { inQuotes = false; }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else if (ch !== '\r') {
      field += ch;
    }
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/** Maps CSV rows onto import fields using the header row, case-insensitively. */
function rowsToImportRecords(rows) {
  if (rows.length === 0) return { records: [], missingHeaders: IMPORT_COLUMNS };
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const indexOf = (col) => header.indexOf(col.toLowerCase());
  const missingHeaders = ['name', 'email'].filter((c) => indexOf(c) === -1);
  if (missingHeaders.length > 0) return { records: [], missingHeaders };

  const records = rows.slice(1).map((cells) => {
    const record = {};
    IMPORT_COLUMNS.forEach((col) => {
      const idx = indexOf(col);
      record[col] = idx === -1 ? '' : (cells[idx] || '').trim();
    });
    return record;
  });
  return { records, missingHeaders: [] };
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | user object
  const [form, setForm] = useState(EMPTY_USER);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkTier, setBulkTier] = useState('');
  const [applyingBulk, setApplyingBulk] = useState(false);

  // CSV import wizard: null | 'select' | 'preview' | 'done'
  const [importStep, setImportStep] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [importRows, setImportRows] = useState([]);
  const [importError, setImportError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchUsers = async () => {
    try {
      const res = await fetch('/api/manage-users', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load users');
      setUsers(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleEdit = (user) => {
    setEditing(user);
    setForm({
      ...EMPTY_USER,
      ...user,
      credits: Array.isArray(user.credits) ? user.credits : [],
      referredBy: user.referredBy || '',
    });
    setError('');
  };

  const handleNew = () => {
    setEditing('new');
    setForm(EMPTY_USER);
    setError('');
  };

  const handleCancel = () => {
    setEditing(null);
    setForm(EMPTY_USER);
    setError('');
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ─── Credits editing ───
  const addCredit = () => {
    setForm({ ...form, credits: [...(form.credits || []), { amount: '', label: '', used: false }] });
  };

  const updateCredit = (index, field, value) => {
    const next = [...(form.credits || [])];
    next[index] = { ...next[index], [field]: value };
    setForm({ ...form, credits: next });
  };

  const removeCredit = (index) => {
    setForm({ ...form, credits: (form.credits || []).filter((_, i) => i !== index) });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const isNew = editing === 'new';
      const url = isNew ? '/api/manage-users' : `/api/manage-users/${editing.id}`;
      const method = isNew ? 'POST' : 'PUT';

      // '__pick__' is a UI-only sentinel meaning "referred, but no referrer chosen yet"
      const payload = {
        ...form,
        referredBy: form.referredBy && form.referredBy !== '__pick__' ? form.referredBy : null,
        credits: (form.credits || []).filter((c) => c.amount !== '' && !Number.isNaN(Number(c.amount))),
      };

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!res.ok) {
        const text = await res.text();
        let msg = 'Save failed';
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }

      handleCancel();
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.name}" (${user.email})?`)) return;

    try {
      const res = await fetch(
        `/api/manage-users/${user.id}?email=${encodeURIComponent(user.email)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Delete failed';
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aVal = (a[sortField] || '').toString().toLowerCase();
      const bVal = (b[sortField] || '').toString().toLowerCase();
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, sortField, sortDirection]);

  const sortIndicator = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // ─── Bulk selection ───
  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const allSelected = users.length > 0 && selectedIds.length === users.length;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : users.map((u) => u.id));
  };

  const handleBulkTier = async () => {
    if (selectedIds.length === 0) {
      setError('Select at least one user first.');
      return;
    }
    if (!window.confirm(`Set tier to "${bulkTier || 'No tier'}" for ${selectedIds.length} user(s)?`)) return;

    setApplyingBulk(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/manage-users-bulk', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds: selectedIds, tier: bulkTier }),
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Bulk update failed';
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }
      const data = await res.json();
      setMessage(`Updated ${data.updated} user(s).`);
      setSelectedIds([]);
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setApplyingBulk(false);
    }
  };

  const referrerName = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? u.name : '—';
  };

  // ─── CSV export ───
  const handleExport = () => {
    const lines = [EXPORT_COLUMNS.join(',')];
    users.forEach((u) => {
      const creditTotal = (Array.isArray(u.credits) ? u.credits : [])
        .filter((c) => !c.used)
        .reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
      lines.push([
        u.name, u.email, u.tier, u.role, u.avatarUrl,
        u.referredBy ? referrerName(u.referredBy) : '',
        u.lastLoginAt || '', u.loginCount || 0, creditTotal.toFixed(2),
      ].map(toCsvValue).join(','));
    });

    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ridgesports-users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ─── CSV import wizard ───
  const openImport = () => {
    setImportStep('select');
    setImportFileName('');
    setImportRows([]);
    setImportError('');
    setImportResult(null);
  };

  const closeImport = () => {
    setImportStep(null);
    setImportRows([]);
    setImportError('');
    setImportResult(null);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setImportError('');
    setImportFileName(file.name);

    try {
      const text = await file.text();
      const { records, missingHeaders } = rowsToImportRecords(parseCsv(text));
      if (missingHeaders.length > 0) {
        setImportError(`The file is missing required column(s): ${missingHeaders.join(', ')}.`);
        return;
      }
      if (records.length === 0) {
        setImportError('No data rows were found in that file.');
        return;
      }

      const existingEmails = new Set(users.map((u) => (u.email || '').toLowerCase()));
      const seenInFile = new Set();

      const classified = records.map((record) => {
        const email = record.email.toLowerCase();
        let status = 'new';
        let reason = '';
        if (!record.name || !email) {
          status = 'invalid'; reason = 'Missing name or email';
        } else if (!EMAIL_RE.test(email)) {
          status = 'invalid'; reason = 'Invalid email address';
        } else if (existingEmails.has(email)) {
          status = 'duplicate'; reason = 'Already a member';
        } else if (seenInFile.has(email)) {
          status = 'duplicate'; reason = 'Repeated in this file';
        }
        if (status === 'new') seenInFile.add(email);
        return { ...record, email, status, reason };
      });

      setImportRows(classified);
      setImportStep('preview');
    } catch {
      setImportError('That file could not be read.');
    }
  };

  const newImportRows = importRows.filter((r) => r.status === 'new');

  const handleRunImport = async () => {
    setImporting(true);
    setImportError('');
    try {
      const res = await fetch('/api/manage-users-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          users: newImportRows.map(({ name, email, tier, role, avatarUrl }) => ({ name, email, tier, role, avatarUrl })),
        }),
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Import failed';
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }
      const data = await res.json();
      setImportResult(data);
      setImportStep('done');
      await fetchUsers();
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <div className="page"><div className="loading">Loading...</div></div>;

  return (
    <div className="page admin-page">
      <div className="admin-header">
        <h1>Manage Users</h1>
        {!editing && (
          <div className="admin-header-actions">
            <button type="button" className="btn" onClick={handleExport}>Export CSV</button>
            <button type="button" className="btn" onClick={openImport}>Import CSV</button>
            <button className="btn btn-primary" onClick={handleNew}>+ Add User</button>
          </div>
        )}
      </div>

      <p className="admin-subtitle">
        There must always be at least one admin. Session lengths (including the 7-day admin default)
        are configured in <a href="/admin/general">General Settings</a>.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {importStep && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Import users from CSV">
          <div className="modal modal-wide">
            <h3 className="modal-title">Import Users from CSV</h3>

            {importError && <div className="alert alert-error">{importError}</div>}

            {importStep === 'select' && (
              <div className="modal-body">
                <p>
                  Upload a CSV with a header row. Required columns: <code>name</code> and <code>email</code>.
                  Optional: <code>tier</code>, <code>role</code>, <code>avatarUrl</code>.
                </p>
                <p className="import-note">
                  Only rows with an email address that is not already a member will be imported —
                  duplicates are skipped and existing users are never modified.
                </p>
                <input type="file" accept=".csv,text/csv" onChange={handleImportFile} />
              </div>
            )}

            {importStep === 'preview' && (
              <div className="modal-body">
                <p>
                  <strong>{importFileName}</strong> — {newImportRows.length} new user
                  {newImportRows.length === 1 ? '' : 's'} will be imported,
                  {' '}{importRows.length - newImportRows.length} will be skipped.
                </p>
                <div className="picker-table-wrap import-preview">
                  <table className="picker-table">
                    <thead>
                      <tr>
                        <th scope="col">Status</th>
                        <th scope="col">Name</th>
                        <th scope="col">Email</th>
                        <th scope="col">Tier</th>
                        <th scope="col">Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importRows.map((r, i) => (
                        <tr key={`${r.email}-${i}`} className={r.status !== 'new' ? 'row-skipped' : undefined}>
                          <td>
                            {r.status === 'new'
                              ? <span className="badge badge-new">New</span>
                              : <span className="badge badge-skip">{r.reason}</span>}
                          </td>
                          <td>{r.name || '—'}</td>
                          <td className="picker-muted">{r.email || '—'}</td>
                          <td>{r.tier || '—'}</td>
                          <td>{r.role || 'user'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {importStep === 'done' && importResult && (
              <div className="modal-body">
                <p><strong>{importResult.created}</strong> user{importResult.created === 1 ? '' : 's'} imported.</p>
                {importResult.skipped && importResult.skipped.length > 0 && (
                  <>
                    <p>{importResult.skipped.length} row(s) skipped:</p>
                    <div className="picker-table-wrap import-preview">
                      <table className="picker-table">
                        <thead>
                          <tr><th scope="col">Email</th><th scope="col">Reason</th></tr>
                        </thead>
                        <tbody>
                          {importResult.skipped.map((s, i) => (
                            <tr key={`${s.email}-${i}`}>
                              <td className="picker-muted">{s.email}</td>
                              <td>{s.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="modal-actions">
              {importStep === 'preview' && (
                <>
                  <button type="button" className="btn" onClick={openImport}>Choose another file</button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleRunImport}
                    disabled={importing || newImportRows.length === 0}
                  >
                    {importing ? 'Importing...' : `Import ${newImportRows.length} user${newImportRows.length === 1 ? '' : 's'}`}
                  </button>
                </>
              )}
              <button type="button" className="btn" onClick={closeImport}>
                {importStep === 'done' ? 'Done' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <form className="admin-form" onSubmit={handleSave}>
          <h2>{editing === 'new' ? 'New User' : 'Edit User'}</h2>
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input id="name" name="name" value={form.name} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input id="email" name="email" type="email" value={form.email} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="avatarUrl">Avatar URL</label>
            <input id="avatarUrl" name="avatarUrl" value={form.avatarUrl} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="tier">Tier</label>
            <select id="tier" name="tier" value={form.tier} onChange={handleChange}>
              <option value="">No tier</option>
              <option value="bronze">Bronze</option>
              <option value="silver">Silver</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="role">Role</label>
            <select id="role" name="role" value={form.role} onChange={handleChange}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="form-group form-group-checkbox">
            <label>
              <input
                type="checkbox"
                checked={!!form.referredBy}
                onChange={(e) => setForm({ ...form, referredBy: e.target.checked ? (form.referredBy || '__pick__') : '' })}
              />
              This user was referred by another user
            </label>
          </div>
          {form.referredBy && (
            <div className="form-group">
              <label htmlFor="referredBy">Referred by</label>
              <select
                id="referredBy"
                name="referredBy"
                value={form.referredBy === '__pick__' ? '' : form.referredBy}
                onChange={(e) => setForm({ ...form, referredBy: e.target.value || '__pick__' })}
              >
                <option value="">— Select a referrer —</option>
                {users
                  .filter((u) => !editing || editing === 'new' || u.id !== editing.id)
                  .map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
              </select>
              <small className="visibility-note">
                When a referred user starts a purchase, the support email is notified.
              </small>
            </div>
          )}

          <div className="form-group">
            <div className="credit-items-header">
              <label className="form-label">Credits</label>
              <button type="button" className="btn btn-sm" onClick={addCredit}>+ Add Credit</button>
            </div>
            {(form.credits || []).length === 0 ? (
              <small className="visibility-hint">No credits assigned.</small>
            ) : (
              <div className="credits-editor">
                {(form.credits || []).map((c, i) => (
                  <div key={c.id || `new-${i}`} className={`credit-row ${c.used ? 'credit-used' : ''}`}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="credit-amount"
                      placeholder="Amount"
                      value={c.amount}
                      onChange={(e) => updateCredit(i, 'amount', e.target.value)}
                    />
                    <input
                      type="text"
                      className="credit-label"
                      placeholder="Label (optional)"
                      value={c.label || ''}
                      onChange={(e) => updateCredit(i, 'label', e.target.value)}
                    />
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={!!c.used}
                        onChange={(e) => updateCredit(i, 'used', e.target.checked)}
                      />
                      Used
                    </label>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => removeCredit(i)}>Remove</button>
                  </div>
                ))}
              </div>
            )}
            <small className="visibility-note">
              Users can apply one unused credit per purchase on credit-eligible items
              (configured in <a href="/admin/general">General Settings</a>).
            </small>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
          </div>
        </form>
      )}

      <div className="bulk-toolbar">
        <span className="bulk-count">{selectedIds.length} selected</span>
        <label htmlFor="bulkTier">Set tier to:</label>
        <select id="bulkTier" value={bulkTier} onChange={(e) => setBulkTier(e.target.value)}>
          <option value="">No tier</option>
          <option value="bronze">Bronze</option>
          <option value="silver">Silver</option>
          <option value="gold">Gold</option>
          <option value="platinum">Platinum</option>
        </select>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleBulkTier}
          disabled={applyingBulk || selectedIds.length === 0}
        >
          {applyingBulk ? 'Applying...' : 'Apply to selected'}
        </button>
        {selectedIds.length > 0 && (
          <button type="button" className="btn btn-sm" onClick={() => setSelectedIds([])}>Clear selection</button>
        )}
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th className="th-check">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                aria-label="Select all users"
              />
            </th>
            <th className="sortable-th" onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
            <th className="sortable-th" onClick={() => handleSort('email')}>Email{sortIndicator('email')}</th>
            <th className="sortable-th" onClick={() => handleSort('tier')}>Tier{sortIndicator('tier')}</th>
            <th className="sortable-th" onClick={() => handleSort('role')}>Role{sortIndicator('role')}</th>
            <th>Credits</th>
            <th>Referred By</th>
            <th className="sortable-th" onClick={() => handleSort('lastLoginAt')}>Last Login{sortIndicator('lastLoginAt')}</th>
            <th className="sortable-th" onClick={() => handleSort('loginCount')}>Logins{sortIndicator('loginCount')}</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedUsers.length === 0 ? (
            <tr><td colSpan={10} className="empty-state">No users yet.</td></tr>
          ) : (
            sortedUsers.map((u) => {
              const userCredits = Array.isArray(u.credits) ? u.credits : [];
              const unused = userCredits.filter((c) => !c.used);
              return (
                <tr key={u.id} className={selectedIds.includes(u.id) ? 'row-selected' : ''}>
                  <td className="td-check">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(u.id)}
                      onChange={() => toggleSelected(u.id)}
                      aria-label={`Select ${u.name}`}
                    />
                  </td>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{u.tier ? <span className={`badge badge-${u.tier}`}>{u.tier}</span> : '—'}</td>
                  <td>{u.role === 'admin' ? <span className="badge badge-admin">Admin</span> : 'User'}</td>
                  <td>
                    {userCredits.length === 0
                      ? '—'
                      : `${unused.length} unused (${money(unused.reduce((sum, c) => sum + Number(c.amount || 0), 0))})`}
                  </td>
                  <td>{u.referredBy ? referrerName(u.referredBy) : '—'}</td>
                  <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</td>
                  <td>{u.loginCount || 0}</td>
                  <td className="td-actions">
                    <button className="btn btn-sm" onClick={() => handleEdit(u)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(u)}>Delete</button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
